import { FC, ReactElement, useState, useMemo } from 'react'
import styled from 'styled-components'
import ListItem from 'components/ListItem'
import { device } from '../../breakpoints'

const months = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
]

interface ListProps {
  data: any
  // handleItemClick: (id: string) => void
  paginate: boolean
  groupByMonth?: boolean
}

const List: FC<ListProps> = ({
  data,
  paginate,
  groupByMonth,
}): ReactElement => {
  const [maxMonth, setMaxMonth] = useState(1)
  const [maxYear, setMaxYear] = useState(1)

  const monthGroups = useMemo(
    () =>
      data.reduce((acc: any, item: any) => {
        if (acc.has(item.year)) {
          const year = acc.get(item.year)

          if (year.has(item.month)) {
            return acc.set(
              item.year,
              year.set(item.month, [...year.get(item.month), item])
            )
          }
          return acc.set(item.year, year.set(item.month, [item]))
        } else {
          return acc.set(item.year, new Map().set(item.month, [item]))
        }
      }, new Map()),
    [data]
  )

  if (monthGroups.size === 0) {
    return <p></p>
  }

  const currentYear = maxYear - 1
  // How many months are available in the current year?
  const monthsNum = monthGroups.get([...monthGroups.keys()][currentYear]).size

  // When we reach the last month of the current year, we need to switch to the next year
  const allowShowMore =
    maxYear < monthGroups.size ||
    (maxYear === monthGroups.size && maxMonth < monthsNum)

  return (
    <Container>
      {groupByMonth ? (
        [...monthGroups.keys()]
          .slice(0, paginate ? maxYear : monthGroups.size)
          .map((year: any, yearIndex: number) => (
            <YearContainer key={`year-${yearIndex}`}>
              {[...monthGroups.get(year).keys()]
                .sort((a, b) => a - b)
                .reverse()
                .slice(
                  0,
                  paginate
                    ? maxMonth + (yearIndex < maxYear - 1 ? 12 : 0)
                    : monthGroups.get(year).size
                )
                .map((month: any, monthIndex: number) => (
                  <MonthContainer key={`year-${yearIndex}-month-${monthIndex}`}>
                    <Title>
                      {months[month]} {year}
                    </Title>
                    <ItemsContainer>
                      {monthGroups
                        .get(year)
                        .get(month)
                        .map((item: any, index: number) => (
                          <ListItem
                            imageUrl={item.imageUrl}
                            id={item.id}
                            metadataId={item.metadataId}
                            key={`item-${item.id}`}
                            type={item.type}
                            titleHeader={item.titleHeader}
                            title={item.title}
                            short={item.lead}
                            description={item.texte}
                            authorRating={item.note}
                            videoId={item['Youtube ID']}
                            trailerUrl={
                              'https://www.youtube.com/embed/' +
                              item['Youtube ID']
                            }
                            startDate={item['date de lancement']}
                            links={item.providerLinks}
                            favorite={item['favorite']}
                            highlighted={item['highlighted'] === 'TRUE'}
                            alreadySeen={item['à revoir'] === 'TRUE'}
                            providers={item.providerLinks}
                          />
                        ))}
                    </ItemsContainer>
                  </MonthContainer>
                ))}
            </YearContainer>
          ))
      ) : (
        <ItemsContainer style={{ marginTop: '2rem' }}>
          {data.map((item: any, index: number) => (
            <ListItem
              // handleClick={(id: string) => {
              //   handleItemClick(id)
              // }}
              imageUrl={item.imageUrl}
              id={item.id}
              metadataId={item.metadataId}
              key={`item-search-${item.id}-${index}`}
              type={item.type}
              title={item.title}
              titleHeader={item.titleHeader}
              short={item.lead}
              description={item.texte}
              authorRating={item.note}
              videoId={item['Youtube ID']}
              trailerUrl={'https://www.youtube.com/embed/' + item['Youtube ID']}
              startDate={item['date de lancement']}
              links={item.providerLinks}
              favorite={item['favorite'] === true}
              highlighted={item['highlighted'] === true}
              alreadySeen={item['à revoir'] === true}
              providers={item.providerLinks}
            />
          ))}
        </ItemsContainer>
      )}
      {allowShowMore && paginate && (
        <ShowMoreContainer>
          <SecondaryButton
            onClick={() => {
              const newMonthIndex = maxMonth + 1
              setMaxMonth(maxMonth < monthsNum ? newMonthIndex : 1)
              setMaxYear(maxMonth >= monthsNum ? maxYear + 1 : maxYear)
            }}
          >
            Voir plus de séries
          </SecondaryButton>
        </ShowMoreContainer>
      )}
    </Container>
  )
}

const SecondaryButton = styled.button`
  background-color: ${({ theme }) => theme.primaryButtonBackground};
  color: ${({ theme }) => theme.primaryButtonText};

  position: relative;
  z-index: 0;
  display: block;
  height: 4.5rem;
  padding: 1rem 2rem;
  overflow: hidden;
  font-weight: 700;
  font-size: 1.5rem;
  font-family: var(--font-plex);
  line-height: 1.7;
  letter-spacing: 0.02rem;
  white-space: nowrap;
  text-align: center;
  text-decoration: none;
  text-overflow: ellipsis;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`

const ShowMoreContainer = styled.div`
  margin-top: 5rem;
  margin-bottom: 5rem;
  display: flex;
  justify-content: center;
`

const Container = styled.div`
  font-family: var(--font-noto);
  font-size: 1.8rem;
  line-height: 3rem;
`

const YearContainer = styled.div``

const MonthContainer = styled.div`
  border-top: 3px solid ${({ theme }) => theme.colorText};
  margin-top: 5rem;
`

const ItemsContainer = styled.div`
  display: grid;
  grid-gap: 1px;
  background-color: #aaaaaa22;
  padding-bottom: 1px;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    grid-template-columns: repeat(1, 1fr);
  }

  @media ${device.tablet}, @media ${device.laptop} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const Title = styled.h2`
  margin-top: 1.2rem;
  color: ${({ theme }) => theme.colorText};
  font-weight: 700;
  font-size: 2rem;
  font-family: var(--font-plex);
  letter-spacing: 0.1rem;
  text-transform: uppercase;
  -webkit-font-smoothing: antialiased;
`

export default List
